import {t} from "@lingui/core/macro";
import Handlebars from "handlebars";
import memoize from "lodash.memoize";

import type {ApolloGqlClient} from "@repo/data-gql-apollo";
import Log, {Errors} from "@repo/logging";
import {PermissionsInfo} from "@repo/shared-core";
import {staticUrl} from "@repo/urls";

import Badges from "#badges-package/badges.ts";
import $ from "#jquery/jquery.ts";
import Profile from "#profile-nav-package/profile.tsx";
// eslint-disable-next-line no-restricted-imports -- TODO: Replace this component with the equivalent one from Wonder Blocks.
import deprecatedReactRender from "#rendering/deprecated-react-render.tsx";
import Gql from "#scratchpads-package/gql.ts";
import {ProjectsAuthoredByUserFetcher} from "#scratchpads-package/project-list-fetchers/projects-authored-by-user-fetcher.ts";

import getAchievementsTemplate from "./achievements.handlebars.ts";
import BadgeDisplayCase from "./badge-display-case.ts";
import getErrorTabNotAccessibleTemplate from "./error-tab-not-accessible.handlebars.ts";
import {
    getCombinedBadges,
    getPublicBadges,
    type CombinedBadges,
    type UserBadge,
} from "./get-badges.ts";
import {isActivityAccessible} from "./is-activity-accessible.ts";
import NoProjectsMessage from "./no-projects-message.tsx";
import {timeAgo} from "./timeago-util.ts";
import {updateRewards} from "./update-rewards.ts";

type BadgeCategory =
    | "BRONZE"
    | "SILVER"
    | "GOLD"
    | "PLATINUM"
    | "DIAMOND"
    | "MASTER";

type BadgeCollection = {
    category: BadgeCategory;
    categoryDescription: string;
    userBadges: Array<UserBadge>;
};

type templateData = {
    badgeCollections: Array<BadgeCollection>;
    fStandardView: boolean;
    breakpoint: any;
    userIsLoggedIn: boolean;
};

interface IProfileTabs {
    achievementsInitialized?: boolean;
    displayCase?: typeof BadgeDisplayCase | null | undefined;
    resetDisplayCase: () => void;
    populateUserCard: (gqlClient: ApolloGqlClient) => Promise<void>;
    populateAchievements: (
        gqlClient: ApolloGqlClient,
        populateUserCardPromise: Promise<undefined> | null | undefined,
        breakpoint: any,
        isAllBadgesPage?: boolean | null | undefined,
        spotlightBadgeSlug?: string | null | undefined,
    ) => any;
    _selectBadgeCategory: ($section: any) => void;
    _showAllCategories: () => void;
    _hideAllCategories: () => void;
    populatePrograms: () => void;
}

const createBadgeCollections = (
    badges: CombinedBadges,
): Array<BadgeCollection> => {
    const collections: Record<BadgeCategory, BadgeCollection> = {
        BRONZE: {
            category: "BRONZE",
            categoryDescription: t`Meteorite badges are common and easy to earn when just getting started.`,
            userBadges: [],
        },
        SILVER: {
            category: "SILVER",
            categoryDescription: t`Moon badges are uncommon and represent an investment in learning.`,
            userBadges: [],
        },
        GOLD: {
            category: "GOLD",
            categoryDescription: t`Earth badges are rare. They require a significant amount of learning.`,
            userBadges: [],
        },
        PLATINUM: {
            category: "PLATINUM",
            categoryDescription: t`Sun badges are epic. Earning them is a true challenge, and they require impressive dedication.`,
            userBadges: [],
        },
        DIAMOND: {
            category: "DIAMOND",
            categoryDescription: t`Black Hole badges are legendary and unknown. They are the rarest Khan Academy awards.`,
            userBadges: [],
        },
        MASTER: {
            category: "MASTER",
            categoryDescription: t`Challenge Patches are special awards for completing topic challenges.`,
            userBadges: [],
        },
    };
    const earnedSlugs = new Set<string>();
    for (const userBadge of badges.userBadges) {
        earnedSlugs.add(userBadge.badge.slug);
        const category = getBadgeCategory(userBadge.badge.badgeCategory);
        collections[category].userBadges.push(userBadge);
    }

    for (const userBadge of badges.allBadges) {
        if (!earnedSlugs.has(userBadge.badge.slug)) {
            const category = getBadgeCategory(userBadge.badge.badgeCategory);
            collections[category].userBadges.push(userBadge);
        }
    }

    // Sort earned badges by lastEarnedDate (most recent first). Sort unearned
    // badges to the end and then by points (ascending).
    const comparator = (a: UserBadge, b: UserBadge): number => {
        if (a.badge.isOwned && b.badge.isOwned) {
            // @ts-expect-error - TS2362 - The left-hand side of an arithmetic operation must be of type 'any', 'number', 'bigint' or an enum type. | TS2363 - The right-hand side of an arithmetic operation must be of type 'any', 'number', 'bigint' or an enum type.
            return new Date(b.lastEarnedDate) - new Date(a.lastEarnedDate);
        }
        if (a.badge.isOwned) {
            return -1;
        }
        if (b.badge.isOwned) {
            return 1;
        }

        const pointsA = a.badge.points || Number.MAX_SAFE_INTEGER;
        const pointsB = b.badge.points || Number.MAX_SAFE_INTEGER;
        return pointsA - pointsB;
    };
    collections.BRONZE.userBadges.sort(comparator);
    collections.SILVER.userBadges.sort(comparator);
    collections.GOLD.userBadges.sort(comparator);
    collections.PLATINUM.userBadges.sort(comparator);
    collections.DIAMOND.userBadges.sort(comparator);
    collections.MASTER.userBadges.sort(comparator);

    return [
        collections.BRONZE,
        collections.SILVER,
        collections.GOLD,
        collections.PLATINUM,
        collections.DIAMOND,
        collections.MASTER,
    ];
};

export const getBadgeCategory = (badgeCategory: number): BadgeCategory => {
    switch (badgeCategory) {
        case 0:
            return "BRONZE";
        case 1:
            return "SILVER";
        case 2:
            return "GOLD";
        case 3:
            return "PLATINUM";
        case 4:
            return "DIAMOND";
        case 5:
            return "MASTER";
        default:
            Log.error(
                `Invalid badgeCategory: ${badgeCategory}`,
                Errors.Internal,
            );
            return "BRONZE";
    }
};

const getBadgeInfo = memoize(() => ({
    BRONZE: {
        icon: staticUrl("/images/badges/meteorite-medium.png"),
        className: "bronze",
        label: t`Meteorite`,
    },
    SILVER: {
        icon: staticUrl("/images/badges/moon-medium.png"),
        className: "silver",
        label: t`Moon`,
    },
    GOLD: {
        icon: staticUrl("/images/badges/earth-medium.png"),
        className: "gold",
        label: t`Earth`,
    },
    PLATINUM: {
        icon: staticUrl("/images/badges/sun-medium.png"),
        className: "platinum",
        label: t`Sun`,
    },
    DIAMOND: {
        icon: staticUrl("/images/badges/eclipse-medium.png"),
        className: "diamond",
        label: t`Black Hole`,
    },
    MASTER: {
        icon: staticUrl("/images/badges/master-challenge-blue-medium.png"),
        className: "master",
        label: t`Challenge`,
    },
}));

export const getBadgeLabel = (
    category: BadgeCategory,
    fStandardView: boolean,
    plural?: boolean,
): string => {
    let label = getBadgeInfo()[category].label;

    if (fStandardView) {
        if (label === t`Challenge`) {
            label = plural ? t`Challenge Patches` : t`Challenge Patch`;
        } else {
            if (plural) {
                label = t`${label} Badges`;
            } else {
                label = t`${label} Badge`;
            }
        }
    }
    return label;
};

/**
 * Utilities to populate and render content into the various profile related
 * tabs viewable from the profile page and meta-dashboard.
 */
const ProfileTabs: IProfileTabs = {
    resetDisplayCase: function (): undefined {
        if (ProfileTabs.displayCase) {
            ProfileTabs.displayCase = undefined;
        }
    },

    populateUserCard: function (gqlClient: ApolloGqlClient): Promise<void> {
        if (ProfileTabs.displayCase) {
            return Promise.resolve();
        }

        return getPublicBadges(
            gqlClient,
            Profile.getBaseRequestParams().kaid,
        ).then((publicBadges) => {
            const publicBadgeList = new Badges.BadgeList(publicBadges);
            const displayCase = new BadgeDisplayCase({
                model: publicBadgeList,
            });
            ProfileTabs.displayCase = displayCase;
            // The display case is now rendered in `UserSummaryWidget` in
            // `profile-widgets.jsx`
        });
    },

    // populateAchievements fetches required badge data, munges the data into
    // the required formated, and renders the badges handlebars template. If we
    // are viewing a badge highlight page (like /badges/:slug) the returned
    // Promise<?UserBadge> will include the metadata needed to render that
    // badge.
    populateAchievements: function (
        gqlClient: ApolloGqlClient,
        populateUserCardPromise?: Promise<undefined> | null,
        breakpoint: string | null = "md",
        isAllBadgesPage: boolean | null = false,
        spotlightBadgeSlug?: string | null,
    ): Promise<UserBadge | null | undefined> {
        // The /badges page is always accessible, even if logged out.
        const accessiblePromise = isAllBadgesPage
            ? Promise.resolve(true)
            : isActivityAccessible(gqlClient);
        const badgeInfo = getBadgeInfo();

        return accessiblePromise.then((accessible) => {
            if (!accessible) {
                const errorTabNotAccessibleTemplate =
                    getErrorTabNotAccessibleTemplate();
                $("#tab-content-achievements").html(
                    errorTabNotAccessibleTemplate(),
                );
                return Promise.resolve(null);
            }

            // Asynchronously load the full badge information in the background.
            return getCombinedBadges(
                gqlClient,
                Profile.getBaseRequestParams().kaid,
                spotlightBadgeSlug,
            ).then((combinedBadges) => {
                ProfileTabs.achievementsInitialized = true;
                const collections = createBadgeCollections(combinedBadges);

                // This block is only needed to populate the badge-selection
                // pane in the UserSummaryWidget (see componentDidMount). To
                // see this pane click on the badge icons in the widget.
                // Callers outside of there may pass a null for
                // populateUserCardPromise in which case we can skip this
                // block. Also, this is only editable if you're viewing your
                // own profile.
                if (populateUserCardPromise && Profile.profile?.isEditable()) {
                    const fullBadgeList = new Badges.UserBadgeList();
                    $.each(collections, function (i, collection) {
                        $.each(collection.userBadges, function (j, badge) {
                            // Collections include unowned badges, filter those
                            // out so folks can't try to display badges they
                            // haven't owned in their display-case.
                            if (badge.badge.isOwned) {
                                fullBadgeList.add(new Badges.UserBadge(badge));
                            }
                        });
                    });

                    populateUserCardPromise.then(
                        ProfileTabs.displayCase?.setFullBadgeList(
                            fullBadgeList,
                        ),
                    );
                }

                // TODO: make the rendering of the full badge page use the models above
                // and consolidate the information

                Handlebars.registerHelper(
                    "toMediumIconSrc",
                    function (category) {
                        // NOTE: all these have already had staticUrl()
                        // called on them.
                        return badgeInfo[category].icon;
                    },
                );

                Handlebars.registerHelper(
                    "toBadgeClassName",
                    function (category) {
                        const val = badgeInfo[category].className;
                        return val;
                    },
                );

                Handlebars.registerHelper("toBadgeLabel", getBadgeLabel);

                Handlebars.registerHelper("getTimeAgo", function (date) {
                    return t`Last achieved ${timeAgo(date)}`;
                });

                $.each(collections, function (collectionIndex, collection) {
                    collection["countEarned"] = 0;
                    collection["className"] =
                        badgeInfo[collection.category]["className"];
                    $.each(
                        collection.userBadges,
                        function (badgeIndex, userBadge) {
                            collection["countEarned"] += userBadge["count"];
                            userBadge["hasMultiple"] = userBadge["count"] > 1;
                        },
                    );
                });

                const data: templateData = {
                    badgeCollections: collections,
                    // TODO: what about mobile-view?
                    fStandardView: true,
                    breakpoint,
                    userIsLoggedIn: PermissionsInfo.isLoggedIn,
                };

                const achievementsTemplate = getAchievementsTemplate();
                $("#tab-content-achievements").html(
                    achievementsTemplate(data, {
                        helpers: {
                            staticUrl,
                        },
                    }),
                );

                $("#achievements #update-badges").click(function () {
                    $("#achievements #update-badges")
                        .blur()
                        .addClass("loading")
                        .text(t`Checking...`);

                    const whenComplete = () => {
                        $("#achievements #update-badges")
                            .removeClass("loading")
                            .text(t`Check for new badges and avatars`);
                    };

                    updateRewards(gqlClient, whenComplete);
                });

                $("#achievements .badge-category-trigger").click(function (
                    this: any,
                    e,
                ) {
                    e.preventDefault();
                    // eslint-disable-next-line no-invalid-this
                    const $section = $(this).parent();
                    ProfileTabs._selectBadgeCategory($section);
                });

                // Start with showing all badge categories
                ProfileTabs._showAllCategories();

                return combinedBadges.spotlightBadge;
            });
        });
    },

    /**
     * Select a category of badges to show in profileroot/achievements.
     * Accepts $section, which corresponds to the section header, such as
     * "Earth Badges"
     */
    _selectBadgeCategory: function ($section: any): undefined {
        const $elContainer = $("#badge-container");

        const showingAll = $($elContainer).hasClass("showing-all");
        if (showingAll) {
            ProfileTabs._hideAllCategories();
        }

        const category = $section.attr("data-category");

        $(".badge-category.selected").removeClass("selected");
        $("#badge-container").css("display", "");

        if ($("#" + category).is(":visible")) {
            // The user clicked the category button for the already displaying category.
            // Hide the current selection and instead show all possible badges!
            $("#badge-container > #" + category).hide();
            $("#badge-container").css("display", "none");
            $section.removeClass("selected");

            ProfileTabs._showAllCategories();
        } else {
            $($elContainer).children().hide();
            $("#" + category, $elContainer).show();

            $section.addClass("selected");
        }
    },

    _showAllCategories: function (): undefined {
        const $elContainer = $("#badge-container");
        $($elContainer).css("display", "");
        $($elContainer).removeClass("single-category");
        $($elContainer).addClass("showing-all");

        const categories = Object.keys(Badges.Category);
        categories.forEach((category) => {
            $("#category-" + category, "#badge-container").show();
        });
    },

    _hideAllCategories: function (): undefined {
        const $elContainer = $("#badge-container");
        $($elContainer).css("display", "");
        $($elContainer).addClass("single-category");
        $($elContainer).removeClass("showing-all");

        const categories = Object.keys(Badges.Category);
        categories.forEach((category) => {
            $("#category-" + category, "#badge-container").hide();
        });
    },

    populatePrograms: function (): any {
        // @ts-expect-error - TS2350 - Only a void function can be called with the 'new' keyword.
        const d = new $.Deferred();

        Promise.all([
            import("../scratchpads-package/dynamic-project-list.tsx").then(
                (module) => module.default,
            ),
            import(
                "../../stylesheets/scratchpad-challenge-package/scratchpad-challenge.less"
            ),
            import("../../stylesheets/scratchpads-package/scratchpads.less"),
        ]).then(function ([DynamicProjectList]) {
            Profile.showSpinner();

            const profile = Profile.profile;
            const data = Profile.getBaseRequestParams();

            Profile.hideSpinner();

            const handleFullyInteractive = () => d.resolve();

            deprecatedReactRender(
                <Gql>
                    {(gqlFetch) => {
                        return (
                            <DynamicProjectList
                                projectListFetcher={
                                    new ProjectsAuthoredByUserFetcher({
                                        gqlFetch,
                                        kaid: data.kaid,
                                        itemsPerPage: 40,
                                    })
                                }
                                hideNotice={true}
                                defaultSort={
                                    profile?.get("isSelf") ? "recent" : "top"
                                }
                                sorts={["top", "recent"]}
                                noResults={() => (
                                    <NoProjectsMessage
                                        isSelf={profile?.get("isSelf")}
                                    />
                                )}
                                onFullyInteractive={handleFullyInteractive}
                            />
                        );
                    }}
                </Gql>,
                $(".profile-scratchpads-list")[0],
            );
        });

        return d.promise();
    },
};

export default ProfileTabs;
